
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import SiteInList from '@/shared/core/model/SiteInList';
import Site from '@/shared/core/model/Site';

import { Getters } from '../../core/store/getters';
import { Mutations } from '../../core/store/mutations';
import { ArrayLengthRule, ValidationRule } from '../../core/validation/validation.rules';
import { Service } from '../model/Service';

import Base from '../../core/components/Base.vue';
import { SnackType } from '../../core/events';
import DateSelectorField from './DateSelectorField.vue';
import { DATE_CONFIG } from './menus.constants';
import ProductDelete from './ProductDelete.vue';

import ProductSearchField from './ProductSearchField.vue';
import { menusService } from '../services/menus.service';
import MenuService from './MenuService.vue';

@Component({
  components: {
    MenuService,
    DateSelectorField,
    ProductSearchField,
    ProductDelete,
  },
})
export default class MenuFormDialogDuplicate extends Base {
  menuRange = false;

  menuDest = false;

  valid = false;

  selectedService: Service = this.$store.getters[`menu/${Getters.SERVICE}`];

  selectedRange: string[] = [];

  productTypesNames: [string, string][] = [];

  selectedProductTypes: string[] = [];

  selectedDate = '';

  selectedCustomers: SiteInList[] = [];

  selectableSites: SiteInList[] = [];

  managedSites: SiteInList[] = [];

  srcSite: Site | null = null;

  srcServices: Service[] = [];

  selectedSrcService: Service | null = null;

  destSites: Site[] | null = [];

  selectedDestServices: Record<string, Service> = {};

  today = new Date();

  minDate = new Date();

  serviceSource: Service = this.$store.getters[`menu/${Getters.SERVICE}`];

  availableDates: Set<string> = new Set();

  isLoadingDates = false;

  @Watch('formDialog')
  async opening(newValue: boolean, oldValue: boolean): Promise<void> {
    if (newValue && !oldValue) {
      this.filterSite();
      await this.loadAvailableDates();
    }
  }

  @Watch('srcSite')
  async OnSrcSiteChange(site: Site) {
    this.srcServices = site.services;
    if (this.srcServices.length === 1) {
      [this.selectedSrcService] = this.srcServices;
    }
    await this.loadAvailableDates();
  }

  @Watch('selectedSrcService')
  async OnSrcServiceChange() {
    await this.loadAvailableDates();
  }

  @Watch('site')
  onSiteChange() {
    this.srcSite = this.site;
  }

  async mounted() {
    const menuConstants = await import(
      `../../../customers/${this.customer}/${this.customer}.constants`
    );
    this.productTypesNames = menuConstants.PRODUCT_TYPES_NAMES;
    this.today.setUTCHours(0, 0, 0, 0);
    this.minDate.setUTCHours(0, 0, 0, 0);
    this.minDate.setUTCDate(this.minDate.getDate() - 90);
    this.managedSites = this.list;
    this.srcSite = this.site;

    if (this.srcSite && this.list.length === 1) {
      if (!this.destSites) {
        this.destSites = [];
      }
      this.destSites.push(this.srcSite);
    }
    this.selectedProductTypes = Object.keys(this.productTypesNames);
  }

  async submit() {
    if (
      !this.site ||
      !this.srcSite ||
      !this.selectedRange.length ||
      !this.selectedSrcService ||
      !this.selectedDate ||
      !this.destSites ||
      !this.selectedDestServices
    ) {
      this.snack('Les conditions de soumissions ne sont pas valides', SnackType.ERROR, 5000);
      return;
    }

    if (!this.destSites?.length) {
      this.snack('Veuillez sélectionner au moins un site de destination', SnackType.ERROR, 5000);
      return;
    }

    const allServicesSelected = this.destSites.every(
      (site) => this.selectedDestServices[site.customerId],
    );

    if (!allServicesSelected) {
      this.snack('Veuillez sélectionner un service pour chaque site', SnackType.ERROR, 5000);
      return;
    }

    const { srcSite, selectedRange, selectedProductTypes, selectedSrcService, selectedDate } = this;

    try {
      const allPromises = this.destSites.map(async (destSite) => {
        const serviceCode = this.selectedDestServices[destSite.customerId].code;

        return menusService
          .duplicateMenus(
            new Date(selectedRange[0]).toISOString(),
            new Date(selectedRange[1]).toISOString(),
            srcSite.customerId,
            selectedSrcService.code,
            new Date(selectedDate).toISOString(),
            [destSite.customerId],
            serviceCode,
            selectedProductTypes,
          )
          .then(() => ({
            serviceCode,
            customerId: destSite.customerId,
            siteName: destSite.name,
          }))
          .catch((error) => {
            console.error('Erreur pour', destSite.name, ':', error.response?.status, error.message);
            return Promise.reject(error);
          });
      });

      const results = await Promise.allSettled(allPromises);

      const succeeded = results.filter(
        (
          r,
        ): r is PromiseFulfilledResult<{
          serviceCode: string;
          customerId: string;
          siteName: string;
        }> => r.status === 'fulfilled',
      );
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

      const failedSiteNames: string[] = [];

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedSite = this.destSites?.[index];
          if (failedSite) {
            failedSiteNames.push(failedSite.name);
            console.error("Détails de l'échec:", {
              site: failedSite.name,
              error: result.reason,
              status: result.reason?.response?.status,
            });
          }
        }
      });

      const updatedMenus = await menusService.getMenus(srcSite.customerId);
      this.$store.commit(`menu/${Mutations.SET_MENUS}`, updatedMenus, { root: true });

      this.close();

      if (failed.length === 0) {
        this.snack(`Menus copiés pour ${succeeded.length} site(s)`);
      } else if (succeeded.length > 0) {
        this.snack(
          `Succès: ${succeeded.length}\nÉchecs: ${failedSiteNames.join(', ')}`,
          SnackType.WARNING,
          15000,
        );
      } else {
        this.snack(
          `Échec pour tous les sites:\n${failedSiteNames.join(', ')}`,
          SnackType.ERROR,
          15000,
        );
      }
    } catch (error: any) {
      this.snack('Erreur lors de la duplication des menus', SnackType.ERROR, 5000);
    } finally {
      this.$store.commit(`core/${Mutations.RELEASE_LOADING}`, { root: true });
    }
  }

  close() {
    this.selectedCustomers = [];
    this.selectedRange = [];
    this.selectedProductTypes = Object.keys(this.productTypesNames);
    this.selectedDate = '';
    if (this.$refs.form) {
      const form = this.$refs.form as HTMLFormElement;
      form.resetValidation();
    }
    this.formDialog = false;
  }

  get allProductTypesSelected(): boolean {
    return this.selectedProductTypes.length === Object.keys(this.productTypesNames).length;
  }

  set formDialog(v: boolean) {
    this.$store.commit(`menu/${Mutations.FORM_DIALOG_DUPLICATE}`, v, { root: true });
  }

  get formDialog(): boolean {
    return this.$store.getters[`menu/${Getters.FORM_DIALOG_DUPLICATE}`];
  }

  get productTypeItems(): unknown[] {
    return Object.entries(this.productTypesNames).map(([typeCode, typeName]) => {
      return { value: typeCode, text: typeName };
    });
  }

  set productTypes(v: string[]) {
    this.selectedProductTypes = v;
  }

  get productTypes(): string[] {
    return this.selectedProductTypes;
  }

  set dateRange(v: string[]) {
    if (v.length > 1) this.menuRange = false;
    this.selectedRange = v;
  }

  get dateRange(): string[] {
    return this.selectedRange;
  }

  get formattedRange(): string {
    if (this.selectedRange.length > 1)
      return `${new Date(this.selectedRange[0]).toLocaleString('fr-FR', DATE_CONFIG)} - ${new Date(
        this.selectedRange[1],
      ).toLocaleString('fr-FR', DATE_CONFIG)}`;
    return '';
  }

  get formattedValue(): string {
    if (this.selectedDate) return new Date(this.selectedDate).toLocaleString('fr-FR', DATE_CONFIG);
    return '';
  }

  get valueForDatepicker(): string {
    if (this.selectedDate) return new Date(this.selectedDate).toISOString().substring(0, 10);
    return '';
  }

  set dateDest(v: string) {
    if (v) this.menuDest = false;
    this.selectedDate = v;
  }

  get dateDest(): string {
    return this.selectedDate;
  }

  set customerIds(sites: SiteInList[]) {
    this.selectedCustomers = sites;
  }

  get customerIds(): SiteInList[] {
    return this.selectedCustomers;
  }

  set isAllSites(event: boolean) {
    this.selectedCustomers = event ? this.list : [];
  }

  get isAllSites() {
    return this.selectedCustomers.length === this.list.length;
  }

  get customersRule(): ValidationRule[] {
    return [ArrayLengthRule];
  }

  set service(service: Service) {
    this.selectedService = service;
  }

  get service(): Service {
    return this.selectedService;
  }

  get list(): Site[] {
    return this.$store.state.core.list;
  }

  makeServiceText(service: Service): string {
    return service.name;
  }

  makeSiteText(site: Site): string {
    return site.name;
  }

  allowedDateRange(val: string) {
    const date = new Date(val);

    if (date.getTime() <= this.minDate.getTime()) {
      return false;
    }

    if (date.getDay() === 0 || date.getDay() === 6) {
      return false;
    }

    const dateStr = date.toISOString().substring(0, 10);
    return this.availableDates.has(dateStr);
  }

  allowedDateDest(val: string) {
    const date = new Date(val);
    date.setUTCHours(0, 0, 0, 0);
    return date.getTime() >= this.today.getTime() && !(date.getDay() === 0 || date.getDay() === 6);
  }

  filterSite() {
    this.serviceSource = this.$store.getters[`menu/${Getters.SERVICE}`];
    this.selectedService = this.$store.getters[`menu/${Getters.SERVICE}`];
    this.selectedCustomers = this.list.filter((site) => site.customerId === this.site?.customerId);
    if (this.services.length < 2) {
      this.selectableSites = this.list.filter((selection) => {
        return selection.services.length === 1;
      });
      this.selectedService = this.serviceSource;

      return this.selectableSites;
    }
    this.selectableSites = this.selectedCustomers;
    return this.selectableSites;
  }

  toggleAllProductTypes(): void {
    if (this.allProductTypesSelected) {
      this.selectedProductTypes = [];
    } else {
      this.selectedProductTypes = Object.keys(this.productTypesNames);
    }
  }

  async loadAvailableDates() {
    if (!this.srcSite || !this.selectedSrcService) {
      return;
    }

    this.isLoadingDates = true;
    try {
      const allMenus = await menusService.getMenus(this.srcSite.customerId);

      const menusForService = allMenus.filter(
        (menu) => menu.serviceCode === this.selectedSrcService?.code,
      );

      this.availableDates = new Set(
        menusForService.map((menu) => {
          const dateStr = new Date(menu.date).toISOString().substring(0, 10);
          return dateStr;
        }),
      );
    } catch (error) {
      console.error('Erreur lors du chargement des dates disponibles:', error);
    } finally {
      this.isLoadingDates = false;
    }
  }
}
